import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tag, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

export default function BrandsPage() {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, name: '' });

  const fetchBrands = async () => {
    try {
      const res = await api.get('/brand-list');
      setBrands(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBrands(); }, []);

  if (user?.system_role !== 'super_admin') return <Navigate to="/dashboard" replace />;

  const handleAdd = async () => {
    if (!dialog.name.trim()) { toast.error('Enter brand name'); return; }
    try {
      await api.post('/brand-list', { name: dialog.name.trim() });
      toast.success('Brand added');
      setDialog({ open: false, name: '' });
      fetchBrands();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"? Staff already mapped to it must be reassigned first.`)) return;
    try {
      await api.delete(`/brand-list/${b.id}`);
      toast.success('Brand deleted');
      fetchBrands();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="brands-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Brand Master</h1>
          <p className="text-neutral-500 text-sm mt-0.5">Manage the brand list used across Achievements, Employees & Targets</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setDialog({ open: true, name: '' })} data-testid="add-brand-button">
          <Plus className="h-4 w-4 mr-2" /> Add Brand
        </Button>
      </div>

      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-neutral-800">Brands ({brands.length})</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2">{[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-neutral-100 rounded-sm animate-pulse" />)}</div>
          ) : brands.length === 0 ? (
            <div className="py-16 text-center text-neutral-500">
              <Tag className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
              <p className="text-sm">No brands yet. Click "Add Brand" to start.</p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {brands.map(b => (
                <div key={b.id} className="flex items-center justify-between py-3" data-testid={`brand-row-${b.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-sm flex items-center justify-center">
                      <Tag className="h-4 w-4 text-amber-700" />
                    </div>
                    <span className="font-medium text-sm">{b.name}</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(b)} data-testid={`delete-brand-${b.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="add-brand-dialog">
          <DialogHeader><DialogTitle className="font-heading font-bold">Add Brand</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-sm">Brand Name</Label>
            <Input value={dialog.name} onChange={e => setDialog(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Samsung" className="rounded-sm" data-testid="brand-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog({ open: false, name: '' })}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="save-brand-button">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
